import {ThemeTokens} from 'app/common/ThemePrefs';
import {Base} from 'app/common/themes/Base';

/**
 * CyberOps theme - Dark cyberpunk/military tactical interface
 * Monochromatic palette with sharp edges and terminal-style aesthetics
 */
export const CyberOps: ThemeTokens = {
  ...Base,

  // Core color tokens
  body: "#E0E0E0",           // Light gray for main text
  emphasis: "#FFFFFF",        // Bright white for emphasized text
  secondary: '#A0A0A0',       // Medium gray for secondary text
  veryLight: "#FFFFFF",       // Always white text

  // Backgrounds - various shades of the primary dark color
  bg: '#0B0B0D',              // Primary background (user specified)
  bgSecondary: '#141416',     // Slightly lighter for panels
  bgTertiary: '#1D1D1F',      // Even lighter for hover states
  bgEmphasis: '#2A2A2C',      // Lightest for selections/emphasis

  // Decorations - using the line color and variations
  decoration: '#6A6A6C',      // Primary line/border color (user specified)
  decorationSecondary: '#5A5A5C',  // Darker variation
  decorationTertiary: '#4A4A4C',   // Even darker variation

  // Primary colors - keeping some accent but muted
  primary: '#FFFFFF',         // White as primary accent
  primaryMuted: '#E0E0E0',    // Light gray as muted primary
  primaryDim: '#C0C0C0',      // Medium gray as dim primary
  primaryEmphasis: '#FFFFFF', // Bright white for emphasis

  // Sharp edges - no rounded corners
  controlBorderRadius: '0px',

  // Cursor and selection - using white/gray theme
  cursor: '#FFFFFF',
  cursorInactive: 'rgba(224,224,224,0.5)',

  // Selection states - subtle gray highlights
  selection: 'rgba(255,255,255,0.1)',
  selectionOpaque: '#2A2A2C',
  selectionDarkerOpaque: '#1D1D1F',
  selectionDarker: 'rgba(255,255,255,0.15)',
  selectionDarkest: 'rgba(255,255,255,0.2)',

  hover: '#6A6A6C',
  backdrop: 'rgba(11,11,13,0.8)',

  // Override font family to monospace/tabular
  fontFamily: `'Courier New', 'Monaco', 'Lucida Console', 'Liberation Mono', monospace`,
  fontFamilyData: `'Courier New', 'Monaco', 'Lucida Console', 'Liberation Mono', monospace`,

  components: {
    ...Base.components,

    /* Text Colors */
    text: '#E0E0E0',
    lightText: '#A0A0A0',
    darkText: '#FFFFFF',
    mediumText: '#C0C0C0',
    disabledText: '#6A6A6C',
    dangerText: '#FF6B6B',
    errorText: '#FF6B6B',
    errorTextHover: '#FF8A8A',

    /* Page Structure */
    pageBg: '#141416',
    pageBackdrop: '#000000',
    mainPanelBg: '#0B0B0D',
    leftPanelBg: '#141416',
    rightPanelBg: '#141416',
    topHeaderBg: '#0B0B0D',
    bottomFooterBg: '#0B0B0D',
    pagePanelsBorder: '#6A6A6C',
    pagePanelsBorderResizing: '#FFFFFF',

    /* Side Panel Openers */
    sidePanelOpenerFg: '#A0A0A0',
    sidePanelOpenerActiveFg: '#FFFFFF',
    sidePanelOpenerActiveBg: '#6A6A6C',

    /* Add New Elements */
    addNewCircleFg: '#0B0B0D',
    addNewCircleBg: '#FFFFFF',
    addNewCircleHoverBg: '#E0E0E0',
    addNewCircleSmallFg: '#0B0B0D',
    addNewCircleSmallBg: '#FFFFFF',
    addNewCircleSmallHoverBg: '#E0E0E0',

    /* Top Bar */
    topBarButtonPrimaryFg: '#FFFFFF',
    topBarButtonSecondaryFg: '#A0A0A0',
    topBarButtonDisabledFg: '#6A6A6C',
    topBarButtonErrorFg: '#FF6B6B',

    /* Notifications */
    notificationsPanelHeaderBg: '#141416',
    notificationsPanelBodyBg: '#0B0B0D',
    notificationsPanelBorder: '#6A6A6C',

    /* Toasts */
    toastBg: '#0B0B0D',
    toastLightText: '#A0A0A0',
    toastText: '#FFFFFF',
    toastMemoText: '#E0E0E0',
    toastMemoBg: '#1D1D1F',
    toastErrorIcon: '#FF6B6B',
    toastErrorBg: '#FF6B6B',
    toastSuccessIcon: '#FFFFFF',
    toastSuccessBg: '#6A6A6C',
    toastWarningIcon: '#FFAA00',
    toastWarningBg: '#FFAA00',
    toastInfoIcon: '#87CEEB',
    toastInfoBg: '#87CEEB',
    toastControlFg: '#FFFFFF',
    toastInfoControlFg: '#87CEEB',

    /* Tooltips */
    tooltipFg: '#FFFFFF',
    tooltipBg: 'rgba(11,11,13,0.95)',
    tooltipIcon: '#A0A0A0',
    tooltipCloseButtonFg: '#FFFFFF',
    tooltipCloseButtonHoverFg: '#0B0B0D',
    tooltipCloseButtonHoverBg: '#FFFFFF',

    /* Modals */
    modalBg: '#0B0B0D',
    modalBackdrop: 'rgba(0,0,0,0.8)',
    modalBorder: '#6A6A6C',
    modalBorderDark: '#4A4A4C',
    modalBorderHover: '#A0A0A0',
    modalInnerShadow: 'rgba(0,0,0,0.5)',
    modalOuterShadow: 'rgba(0,0,0,0.8)',
    modalCloseButtonFg: '#A0A0A0',
    modalBackdropCloseButtonFg: '#FFFFFF',
    modalBackdropCloseButtonHoverFg: '#E0E0E0',

    /* Popups */
    popupBg: '#0B0B0D',
    popupSecondaryBg: '#141416',
    popupInnerShadow: 'rgba(0,0,0,0.5)',
    popupOuterShadow: 'rgba(0,0,0,0.8)',
    popupCloseButtonFg: '#A0A0A0',

    /* Progress Bars */
    progressBarFg: '#FFFFFF',
    progressBarErrorFg: '#FF6B6B',
    progressBarBg: '#6A6A6C',

    /* Prompts */
    promptFg: '#A0A0A0',

    /* Links */
    link: '#FFFFFF',
    linkHover: '#E0E0E0',

    /* Hover Effects */
    hover: '#1D1D1F',
    lightHover: '#2A2A2C',

    /* Cell Editor */
    cellEditorFg: '#FFFFFF',
    cellEditorPlaceholderFg: '#A0A0A0',
    cellEditorBg: '#0B0B0D',

    /* Cursor */
    cursor: '#FFFFFF',
    cursorInactive: 'rgba(224,224,224,0.5)',
    cursorReadonly: '#A0A0A0',

    /* Tables */
    tableHeaderFg: '#FFFFFF',
    tableHeaderSelectedFg: '#FFFFFF',
    tableHeaderBg: '#141416',
    tableHeaderSelectedBg: '#2A2A2C',
    tableHeaderBorder: '#6A6A6C',
    tableBodyBg: '#0B0B0D',
    tableBodyBorder: '#6A6A6C',
    tableAddNewBg: '#1D1D1F',
    tableScrollShadow: 'rgba(0,0,0,0.5)',
    tableFrozenColumnsBorder: '#6A6A6C',
    tableDragDropIndicator: '#FFFFFF',
    tableDragDropShadow: 'rgba(0,0,0,0.3)',
    tableCellSummaryBg: '#1D1D1F',

    /* Cards */
    cardCompactWidgetBg: '#141416',
    cardCompactRecordBg: '#0B0B0D',
    cardBlocksBg: '#141416',
    cardFormLabel: '#A0A0A0',
    cardCompactLabel: '#A0A0A0',
    cardBlocksLabel: '#A0A0A0',
    cardFormBorder: '#6A6A6C',
    cardCompactBorder: '#6A6A6C',
    cardEditingLayoutBg: 'rgba(45,45,47,0.3)',
    cardEditingLayoutBorder: '#6A6A6C',

    /* Card Lists */
    cardListFormBorder: '#6A6A6C',
    cardListBlocksBorder: '#6A6A6C',

    /* Selection */
    selection: 'rgba(255,255,255,0.1)',
    selectionDarker: 'rgba(255,255,255,0.15)',
    selectionDarkest: 'rgba(255,255,255,0.2)',
    selectionOpaqueFg: '#FFFFFF',
    selectionOpaqueBg: '#2A2A2C',
    selectionOpaqueDarkBg: '#1D1D1F',
    selectionHeader: 'rgba(42,42,44,0.5)',

    /* Widgets */
    widgetBg: '#0B0B0D',
    widgetBorder: '#6A6A6C',
    widgetActiveBorder: '#FFFFFF',
    widgetInactiveStripesLight: '#141416',
    widgetInactiveStripesDark: '#1D1D1F',

    /* Pinned Docs */
    pinnedDocFooterBg: '#0B0B0D',
    pinnedDocBorder: '#6A6A6C',
    pinnedDocBorderHover: '#A0A0A0',
    pinnedDocEditorBg: '#141416',

    /* Raw Data */
    rawDataTableBorder: '#6A6A6C',
    rawDataTableBorderHover: '#A0A0A0',

    /* Controls */
    controlFg: '#FFFFFF',
    controlPrimaryFg: '#0B0B0D',
    controlPrimaryBg: '#FFFFFF',
    controlPrimaryHoverBg: '#E0E0E0',
    controlSecondaryFg: '#A0A0A0',
    controlSecondaryDisabledFg: '#6A6A6C',
    controlSecondaryHoverFg: '#FFFFFF',
    controlSecondaryHoverBg: '#6A6A6C',
    controlHoverFg: '#E0E0E0',
    controlDisabledFg: '#6A6A6C',
    controlDisabledBg: '#2A2A2C',
    controlBorder: '1px solid #6A6A6C',

    /* Checkboxes */
    checkboxBg: '#0B0B0D',
    checkboxSelectedFg: '#FFFFFF',
    checkboxDisabledBg: '#2A2A2C',
    checkboxBorder: '#6A6A6C',
    checkboxBorderHover: '#A0A0A0',

    /* Move Docs */
    moveDocsSelectedFg: '#0B0B0D',
    moveDocsSelectedBg: '#FFFFFF',
    moveDocsDisabledFg: '#6A6A6C',

    /* Filter Bar */
    filterBarButtonSavedFg: '#0B0B0D',
    filterBarButtonSavedBg: '#6A6A6C',
    filterBarButtonSavedHoverBg: '#A0A0A0',

    /* Icons */
    iconDisabled: '#6A6A6C',
    iconError: '#FF6B6B',

    /* Icon Buttons */
    iconButtonFg: '#0B0B0D',
    iconButtonPrimaryBg: '#FFFFFF',
    iconButtonPrimaryHoverBg: '#E0E0E0',
    iconButtonSecondaryBg: '#6A6A6C',
    iconButtonSecondaryHoverBg: '#A0A0A0',

    /* Left Panel */
    pageHoverBg: 'rgba(29,29,31,0.5)',
    activePageFg: '#FFFFFF',
    activePageBg: '#2A2A2C',
    disabledPageFg: '#6A6A6C',
    pageOptionsFg: '#A0A0A0',
    pageOptionsHoverFg: '#FFFFFF',
    pageOptionsHoverBg: '#6A6A6C',
    pageOptionsSelectedHoverBg: '#A0A0A0',
    pageInitialsFg: '#0B0B0D',
    pageInitialsBg: '#A0A0A0',
    pageInitialsEmojiBg: '#FFFFFF',
    pageInitialsEmojiOutline: '#6A6A6C',

    /* Right Panel */
    rightPanelTabFg: '#A0A0A0',
    rightPanelTabBg: '#0B0B0D',
    rightPanelTabIcon: '#A0A0A0',
    rightPanelTabIconHover: '#FFFFFF',
    rightPanelTabBorder: '#6A6A6C',
    rightPanelTabHoverBg: '#1D1D1F',
    rightPanelTabHoverFg: '#FFFFFF',
    rightPanelTabSelectedFg: '#FFFFFF',
    rightPanelTabSelectedBg: '#141416',
    rightPanelTabSelectedIcon: '#FFFFFF',
    rightPanelTabButtonHoverBg: '#2A2A2C',
    rightPanelSubtabFg: '#A0A0A0',
    rightPanelSubtabSelectedFg: '#FFFFFF',
    rightPanelSubtabSelectedUnderline: '#FFFFFF',
    rightPanelSubtabHoverFg: '#FFFFFF',
    rightPanelDisabledOverlay: 'rgba(20,20,22,0.8)',
    rightPanelToggleButtonEnabledFg: '#0B0B0D',
    rightPanelToggleButtonEnabledBg: '#FFFFFF',
    rightPanelToggleButtonDisabledFg: '#6A6A6C',
    rightPanelToggleButtonDisabledBg: '#2A2A2C',
    rightPanelFieldSettingsBg: '#141416',
    rightPanelFieldSettingsButtonBg: '#2A2A2C',
    rightPanelCustomWidgetButtonFg: '#FFFFFF',
    rightPanelCustomWidgetButtonBg: '#6A6A6C',

    /* Document History */
    documentHistorySnapshotFg: '#E0E0E0',
    documentHistorySnapshotSelectedFg: '#FFFFFF',
    documentHistorySnapshotBg: '#0B0B0D',
    documentHistorySnapshotSelectedBg: '#2A2A2C',
    documentHistorySnapshotBorder: '#6A6A6C',
    documentHistoryActivityText: '#E0E0E0',
    documentHistoryActivityLightText: '#A0A0A0',
    documentHistoryTableHeaderFg: '#FFFFFF',
    documentHistoryTableBorder: '#6A6A6C',
    documentHistoryTableBorderLight: '#4A4A4C',

    /* Accents */
    accentIcon: '#FFFFFF',
    accentBorder: '#FFFFFF',
    accentText: '#FFFFFF',

    /* Inputs */
    inputFg: '#FFFFFF',
    inputBg: '#0B0B0D',
    inputDisabledFg: '#6A6A6C',
    inputDisabledBg: '#2A2A2C',
    inputPlaceholderFg: '#A0A0A0',
    inputBorder: '#6A6A6C',
    inputValid: '#FFFFFF',
    inputInvalid: '#FF6B6B',
    inputFocus: '#FFFFFF',
    inputReadonlyBg: '#141416',
    inputReadonlyBorder: '#6A6A6C',

    /* Choice Tokens */
    choiceTokenFg: '#FFFFFF',
    choiceTokenBlankFg: '#A0A0A0',
    choiceTokenBg: '#6A6A6C',
    choiceTokenSelectedBg: '#FFFFFF',
    choiceTokenSelectedBorder: '#FFFFFF',
    choiceTokenInvalidFg: '#FFFFFF',
    choiceTokenInvalidBg: '#FF6B6B',
    choiceTokenInvalidBorder: '#FF6B6B',

    /* Choice Entry */
    choiceEntryBg: '#0B0B0D',
    choiceEntryBorder: '#6A6A6C',
    choiceEntryBorderHover: '#A0A0A0',

    /* Select Buttons */
    selectButtonFg: '#FFFFFF',
    selectButtonPlaceholderFg: '#A0A0A0',
    selectButtonBg: '#0B0B0D',
    selectButtonBorder: '#6A6A6C',
    selectButtonBorderInvalid: '#FF6B6B',

    /* Menus */
    menuText: '#A0A0A0',
    menuLightText: '#A0A0A0',
    menuBg: '#0B0B0D',
    menuSubheaderFg: '#FFFFFF',
    menuBorder: '#6A6A6C',
    menuShadow: 'rgba(0,0,0,0.8)',

    /* Menu Items */
    menuItemFg: '#FFFFFF',
    menuItemSelectedFg: '#FFFFF',
    menuItemSelectedBg: '#6A6A6C',
    menuItemDisabledFg: '#424242',
    menuItemIconFg: '#A0A0A0',
    menuItemIconSelectedFg: '#0B0B0D',

    /* Autocomplete */
    autocompleteMatchText: '#FFFFFF',
    autocompleteSelectedMatchText: '#0B0B0D',
    autocompleteItemSelectedBg: '#FFFFFF',
    autocompleteAddNewCircleFg: '#0B0B0D',
    autocompleteAddNewCircleBg: '#FFFFFF',
    autocompleteAddNewCircleSelectedBg: '#E0E0E0',

    /* Search */
    searchBorder: '#6A6A6C',
    searchPrevNextButtonFg: '#A0A0A0',
    searchPrevNextButtonBg: '#1D1D1F',

    /* Loaders */
    loaderFg: '#FFFFFF',
    loaderBg: '#6A6A6C',

    /* Site Switcher */
    siteSwitcherActiveFg: '#0B0B0D',
    siteSwitcherActiveBg: '#FFFFFF',

    /* Doc Menu */
    docMenuDocOptionsFg: '#6A6A6C',
    docMenuDocOptionsHoverFg: '#A0A0A0',
    docMenuDocOptionsHoverBg: '#2A2A2C',

    /* Shortcut Keys */
    shortcutKeyFg: '#FFFFFF',
    shortcutKeyPrimaryFg: '#FFFFFF',
    shortcutKeySecondaryFg: '#A0A0A0',
    shortcutKeyBg: '#0B0B0D',
    shortcutKeyBorder: '#6A6A6C',

    /* Breadcrumbs */
    breadcrumbsTagFg: '#0B0B0D',
    breadcrumbsTagBg: '#6A6A6C',
    breadcrumbsTagAlertBg: '#FF6B6B',

    /* Widget Picker */
    widgetPickerPrimaryBg: '#0B0B0D',
    widgetPickerSecondaryBg: '#141416',
    widgetPickerItemFg: '#FFFFFF',
    widgetPickerItemSelectedBg: '#2A2A2C',
    widgetPickerItemDisabledBg: '#1D1D1F',
    widgetPickerIcon: '#A0A0A0',
    widgetPickerPrimaryIcon: '#FFFFFF',
    widgetPickerSummaryIcon: '#FFFFFF',
    widgetPickerBorder: '#6A6A6C',
    widgetPickerShadow: 'rgba(0,0,0,0.8)',

    /* Code View */
    codeViewText: '#E0E0E0',
    codeViewKeyword: '#FFFFFF',
    codeViewComment: '#A0A0A0',
    codeViewMeta: '#C0C0C0',
    codeViewTitle: '#FFFFFF',
    codeViewParams: '#E0E0E0',
    codeViewString: '#E0E0E0',
    codeViewNumber: '#FFFFFF',
    codeViewBuiltin: '#C0C0C0',
    codeViewLiteral: '#E0E0E0',

    /* Importer */
    importerTableInfoBorder: '#6A6A6C',
    importerPreviewBorder: '#6A6A6C',
    importerSkippedTableOverlay: 'rgba(29,29,31,0.8)',
    importerMatchIcon: '#6A6A6C',
    importerOutsideBg: '#0B0B0D',
    importerMainContentBg: '#141416',
    importerActiveFileBg: '#FFFFFF',
    importerActiveFileFg: '#0B0B0D',
    importerInactiveFileBg: '#6A6A6C',
    importerInactiveFileFg: '#FFFFFF',

    /* Menu Toggles */
    menuToggleFg: '#A0A0A0',
    menuToggleHoverFg: '#6A6A6C',
    menuToggleActiveFg: '#6A6A6C',
    menuToggleBg: '#0B0B0D',
    menuToggleBorder: '#6A6A6C',

    /* Info Button */
    infoButtonFg: '#A0A0A0',
    infoButtonHoverFg: '#FFFFFF',
    infoButtonActiveFg: '#FFFFFF',

    /* Button Groups */
    buttonGroupFg: '#FFFFFF',
    buttonGroupLightFg: '#A0A0A0',
    buttonGroupBg: 'transparent',
    buttonGroupBgHover: '#1D1D1F',
    buttonGroupIcon: '#A0A0A0',
    buttonGroupBorder: '#6A6A6C',
    buttonGroupBorderHover: '#A0A0A0',
    buttonGroupSelectedFg: '#0B0B0D',
    buttonGroupLightSelectedFg: '#0B0B0D',
    buttonGroupSelectedBg: '#FFFFFF',
    buttonGroupSelectedBorder: '#FFFFFF',

    /* Access Rules */
    accessRulesTableHeaderFg: '#FFFFFF',
    accessRulesTableHeaderBg: '#2A2A2C',
    accessRulesTableBodyFg: '#A0A0A0',
    accessRulesTableBodyLightFg: '#6A6A6C',
    accessRulesTableBorder: '#6A6A6C',
    accessRulesColumnListBorder: '#6A6A6C',
    accessRulesColumnItemFg: '#FFFFFF',
    accessRulesColumnItemBg: '#141416',
    accessRulesColumnItemIconFg: '#A0A0A0',
    accessRulesColumnItemIconHoverFg: '#FFFFFF',
    accessRulesColumnItemIconHoverBg: '#6A6A6C',
    accessRulesFormulaEditorBg: '#0B0B0D',
    accessRulesFormulaEditorBorderHover: '#A0A0A0',
    accessRulesFormulaEditorBgDisabled: '#2A2A2C',
    accessRulesFormulaEditorFocus: '#FFFFFF',

    /* Cells */
    cellFg: '#FFFFFF',
    cellBg: '#0B0B0D',
    cellZebraBg: '#141416',

    /* Charts */
    chartFg: '#A0A0A0',
    chartBg: '#0B0B0D',
    chartLegendBg: 'rgba(11,11,13,0.9)',
    chartXAxis: '#A0A0A0',
    chartYAxis: '#A0A0A0',

    /* Comments */
    commentsPopupHeaderBg: '#141416',
    commentsPopupBodyBg: '#0B0B0D',
    commentsPopupBorder: '#6A6A6C',
    commentsUserNameFg: '#FFFFFF',
    commentsPanelTopicBg: '#0B0B0D',
    commentsPanelTopicBorder: '#6A6A6C',
    commentsPanelResolvedTopicBg: '#141416',

    /* Date Picker */
    datePickerSelectedFg: '#0B0B0D',
    datePickerSelectedBg: '#FFFFFF',
    datePickerSelectedBgHover: '#E0E0E0',
    datePickerTodayFg: '#0B0B0D',
    datePickerTodayBg: '#A0A0A0',
    datePickerTodayBgHover: '#C0C0C0',
    datePickerRangeStartEndBg: '#A0A0A0',
    datePickerRangeStartEndBgHover: '#C0C0C0',
    datePickerRangeBg: '#6A6A6C',
    datePickerRangeBgHover: '#A0A0A0',

    /* Tutorials */
    tutorialsPopupBorder: '#6A6A6C',
    tutorialsPopupHeaderFg: '#FFFFFF',
    tutorialsPopupBoxBg: '#141416',
    tutorialsPopupCodeFg: '#FFFFFF',
    tutorialsPopupCodeBg: '#0B0B0D',
    tutorialsPopupCodeBorder: '#6A6A6C',

    /* Ace Editor */
    aceEditorBg: '#0B0B0D',
    aceAutocompletePrimaryFg: '#FFFFFF',
    aceAutocompleteSecondaryFg: '#A0A0A0',
    aceAutocompleteHighlightedFg: '#FFFFFF',
    aceAutocompleteBg: '#0B0B0D',
    aceAutocompleteBorder: '#6A6A6C',
    aceAutocompleteLink: '#FFFFFF',
    aceAutocompleteLinkHighlighted: '#E0E0E0',
    aceAutocompleteActiveLineBg: '#2A2A2C',
    aceAutocompleteLineBorderHover: '#6A6A6C',
    aceAutocompleteLineBgHover: '#1D1D1F',

    /* Color Select */
    colorSelectFg: '#A0A0A0',
    colorSelectBg: '#0B0B0D',
    colorSelectShadow: 'rgba(0,0,0,0.8)',
    colorSelectFontOptionsBorder: '#6A6A6C',
    colorSelectFontOptionFg: '#FFFFFF',
    colorSelectFontOptionBgHover: '#2A2A2C',
    colorSelectFontOptionFgSelected: '#0B0B0D',
    colorSelectFontOptionBgSelected: '#FFFFFF',
    colorSelectColorSquareBorder: '#6A6A6C',
    colorSelectColorSquareBorderEmpty: '#FFFFFF',
    colorSelectInputFg: '#A0A0A0',
    colorSelectInputBg: '#0B0B0D',
    colorSelectInputBorder: '#6A6A6C',

    /* Highlighted Code */
    highlightedCodeBlockBg: '#141416',
    highlightedCodeBlockBgDisabled: '#2A2A2C',
    highlightedCodeFg: '#A0A0A0',
    highlightedCodeBorder: '#6A6A6C',
    highlightedCodeBgDisabled: '#0B0B0D',

    /* Login Page */
    loginPageBg: '#0B0B0D',
    loginPageBackdrop: '#141416',
    loginPageLine: '#6A6A6C',
    loginPageGoogleButtonFg: '#FFFFFF',
    loginPageGoogleButtonBg: '#141416',
    loginPageGoogleButtonBgHover: '#2A2A2C',
    loginPageGoogleButtonBorder: '#6A6A6C',

    /* Formula Assistant */
    formulaAssistantHeaderBg: '#141416',
    formulaAssistantBorder: '#6A6A6C',
    formulaAssistantPreformattedTextBg: '#141416',

    /* Attachments */
    attachmentsEditorButtonFg: '#FFFFFF',
    attachmentsEditorButtonHoverFg: '#E0E0E0',
    attachmentsEditorButtonBg: '#141416',
    attachmentsEditorButtonHoverBg: '#2A2A2C',
    attachmentsEditorButtonBorder: '#6A6A6C',
    attachmentsEditorButtonIcon: '#A0A0A0',
    attachmentsEditorBorder: '#6A6A6C',
    attachmentsCellIconFg: '#A0A0A0',
    attachmentsCellIconBg: '#2A2A2C',
    attachmentsCellIconHoverBg: '#6A6A6C',

    /* Announcement Popups */
    announcementPopupFg: '#FFFFFF',
    announcementPopupBg: '#141416',

    /* Switches */
    switchSliderFg: '#6A6A6C',
    switchCircleFg: '#FFFFFF',

    /* Scroll Shadow */
    scrollShadow: 'rgba(0,0,0,0.5)',

    /* Toggle Checkboxes */
    toggleCheckboxFg: '#A0A0A0',

    /* Numeric Spinners */
    numericSpinnerFg: '#A0A0A0',

    /* Widget Gallery */
    widgetGalleryBorder: '#6A6A6C',
    widgetGalleryBorderSelected: '#FFFFFF',
    widgetGalleryShadow: 'rgba(0,0,0,0.8)',
    widgetGalleryBgHover: '#141416',
    widgetGallerySecondaryHeaderFg: '#FFFFFF',
    widgetGallerySecondaryHeaderBg: '#6A6A6C',
    widgetGallerySecondaryHeaderBgHover: '#A0A0A0',

    /* Markdown Cell */
    markdownCellLightBg: '#141416',
    markdownCellLightBorder: '#0B0B0D',
    markdownCellMediumBorder: '#6A6A6C',

    /* App Header */
    appHeaderBg: '#0B0B0D',
    appHeaderBorder: '#0B0B0D',
    appHeaderBorderHover: '#6A6A6C',

    /* Card Button */
    cardButtonBorder: '#6A6A6C',
    cardButtonBorderSelected: '#FFFFFF',
    cardButtonShadow: 'rgba(0,0,0,0.3)',

    /* Formula Icon */
    formulaIcon: '#A0A0A0',
  }
};